import React, { useEffect, useMemo, useState } from 'react';
import {
  useInfoAggiuntivaBilanciata,
  useInfoAggiuntivaIndex,
  useInfoAggiuntivaMeta,
  useInfoTipo,
  useIsAvvenimento,
} from 'features/sport/hooks';

import { ItemWithTranslation } from 'types/swagger';
import { KeyManagerSport } from 'features/sport';
import { ScommesseDropdownRecursiveItem } from 'components/scommesseTipo/components/scommesseDropdownRecursive/ScommesseDropdownRecursiveItem';
import { ScommesseGroupWrapper } from 'components/scommesseTipo/wrappers/scommesseGroupWrapper';
import { ScommesseLeafItem } from 'components/scommesseTipo/components/cells';
import { ScommesseTableQuotaSelect } from 'components/scommesseTipo/components/scommesseTableQuotaSelect';
import { SizeInfoAggiuntiva } from 'lib/api/sport/sportScommesseBySlugResponse';
import { SkeletonCell } from 'components/scommesseTipo/components/cells/SkeletonCell';
import { calculateBoxNumber } from 'components/scommesseTipo';
import classNames from 'classnames';
import { isTruthy } from 'utility/functions';
import styles from './ScommesseDropdownRecursive.module.scss';
import { useTranslation } from 'hooks/useLingUI';

export type ScommesseDropdownRecursiveProps = {
  counter: number;
  disabled?: boolean;
  isEstesa?: boolean;
  scommessaKey: string;
  hasTitleHeader: boolean;
  followBilanciata?: boolean;
  infoAggiuntivaKey: string;
  sizeInfoAggiuntive: Array<SizeInfoAggiuntiva>;
  infoTipoScommessaId: string;
  gtagAction?: () => void;
} & ItemWithTranslation<{ infoAggHeaders: Array<string> }>;

export const ScommesseDropdownRecursive = (props: ScommesseDropdownRecursiveProps) => {
  const { counter, disabled, isEstesa, scommessaKey, infoAggiuntivaKey, sizeInfoAggiuntive, followBilanciata } = props;
  const { t } = useTranslation();

  const km = useMemo(() => new KeyManagerSport(scommessaKey), [scommessaKey]);
  const tipoScommessa = useInfoTipo(km.tipoScommessaId);
  const isAvvenimento = useIsAvvenimento();

  const [selectedInfoAggKey, setSelectedInfoAggKey] = useState<string | undefined>();

  const { defaultInfoAgg, bilanciataKey, nextInfoAgg, showHeader } = useInfoAggiuntivaBilanciata(infoAggiuntivaKey);

  const infoAggMeta = useInfoAggiuntivaMeta(scommessaKey);
  const infoAggIndex = useInfoAggiuntivaIndex(selectedInfoAggKey);

  useEffect(() => {
    if (!isTruthy(nextInfoAgg?.length)) {
      setSelectedInfoAggKey(undefined);
      return;
    }

    if (!selectedInfoAggKey || !nextInfoAgg?.includes(selectedInfoAggKey)) {
      setSelectedInfoAggKey(defaultInfoAgg);
    }
  }, [nextInfoAgg, defaultInfoAgg, selectedInfoAggKey]);

  useEffect(() => {
    if (!followBilanciata) return;

    if (defaultInfoAgg) {
      if (selectedInfoAggKey === defaultInfoAgg) return;
      setSelectedInfoAggKey(selectedInfoAggKey);
    }

    if (bilanciataKey) {
      if (selectedInfoAggKey == bilanciataKey) return;
      setSelectedInfoAggKey(bilanciataKey);
    }
  }, [defaultInfoAgg, bilanciataKey, selectedInfoAggKey, followBilanciata]);

  const { headerList, type } = useMemo(() => {
    let index = counter ?? 0;
    const { esitoKeyList } = infoAggIndex ?? {};
    const { headers, headersTrKey } = infoAggMeta ?? {};
    const { headers: tipoHeaders, headersTrKey: tipoHeadersTrKey, numeroEsiti } = tipoScommessa ?? {};

    // esito keaders when infoAggiuntiva has esiti
    const list = isTruthy(esitoKeyList?.length)
      ? (tipoHeaders ?? []).map((k, idx) => t(tipoHeadersTrKey?.[idx], k))
      : [];
    list?.unshift(t(headersTrKey?.[index], headers?.[index]));

    return {
      type: (numeroEsiti ?? 0) < 4 ? ScommesseLeafItem.InfoAggiuntivaWithEsito : ScommesseLeafItem.InfoAggiuntiva,
      headerList: list,
    };
  }, [counter, infoAggIndex, infoAggMeta, t, tipoScommessa]);

  return (
    <React.Fragment>
      <ScommesseTableQuotaSelect
        key={`select-${infoAggiuntivaKey}-${selectedInfoAggKey}`}
        type={type}
        size={sizeInfoAggiuntive[counter]}
        counter={counter ?? 0}
        disabled={disabled}
        isEstesa={isEstesa}
        className={classNames(styles.select, {
          [styles.extendedWidth]: isEstesa,
          [styles.selectAvvenimento]: isAvvenimento,
        })}
        containerClassName={classNames({ [styles.selectContainerAvvenimento]: isAvvenimento })}
        showHeader={showHeader}
        headerList={headerList}
        optionsKeys={nextInfoAgg ?? []}
        scommessaKey={scommessaKey}
        bilanciataKey={bilanciataKey}
        infoAggiuntivaKey={selectedInfoAggKey ?? ''}
        selectedOptionKey={selectedInfoAggKey}
        onOptionSelected={setSelectedInfoAggKey}
      />
      {selectedInfoAggKey ? (
        <ScommesseDropdownRecursiveItem
          {...props}
          key={`item-${selectedInfoAggKey}`}
          infoAggiuntivaKey={selectedInfoAggKey}
        />
      ) : (
        <>
          {/* <ScommesseGroupWrapper scommessaKey={scommessaKey}> */}
          {calculateBoxNumber(tipoScommessa)
            .filter((_, idx) => idx > counter)
            .map(({ header, headerTrKey, size, isInfoAgg }, index) => (
              <SkeletonCell
                key={`${headerTrKey}-${index}`}
                size={size}
                type={isInfoAgg ? ScommesseLeafItem.InfoAggiuntiva : ScommesseLeafItem.Esito}
                itemKey={`${infoAggiuntivaKey}-${index}`}
                scommessaKey={scommessaKey}
                infoAggiuntivaKey={infoAggiuntivaKey}
                header={header}
                headerTrKey={headerTrKey}
              />
            ))}
        </>
      )}
    </React.Fragment>
  );
};